import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import { TOKEN_KEY, url, imgUrl } from '../../../../Helpers/utils/Constant';
import Toaster from '../../../../Toaster/Toaster';
import { IAnnoucement } from 'src/Types/module/announcement/announcementType';
import CommonPdfAndImageVeiw from 'src/components/pdfAndImageView/CommonPdfAndImageVeiw';

interface IProps {
  handleClose: Function;
  announcement: IAnnoucement;
  setRefreshTrigger: Function;
}

const UpdateAnnoucement = ({
  handleClose,
  announcement,
  setRefreshTrigger,
}: IProps) => {
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      title: announcement?.title || '',
      content: announcement?.content || '',
      published_date: announcement?.published_date
        ? moment(announcement.published_date).format('YYYY-MM-DD')
        : '',
    },
  });

  const Toast = Toaster();
  const [loading, setLoading] = useState<Boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [deleteAttachments, setDeleteAttachments] = useState<number[]>([]);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (announcement) {
      const formattedDate = announcement.published_date
        ? moment(announcement.published_date).format('YYYY-MM-DD')
        : '';

      reset({
        title: announcement.title || '',
        content: announcement.content || '',
        published_date: formattedDate,
      });
      setDeleteAttachments([]);

      if (Array.isArray(announcement.type)) {
        setSelectedTypes(announcement.type);
      } else if (typeof announcement.type === 'string') {
        setSelectedTypes([announcement.type]);
      } else {
        setSelectedTypes(['NOTICE']);
      }
    }
  }, [announcement, reset]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
      event.target.value = '';
    }
  };

  const handleTypeToggle = (typeName: string) => {
    setSelectedTypes((prev) =>
      prev.includes(typeName)
        ? prev.filter((t) => t !== typeName)
        : [...prev, typeName]
    );
  };

  const onSubmit = async (inputs: any) => {
    if (selectedTypes.length === 0) {
      return Toast.fire({
        icon: 'error',
        title: 'Please select at least one announcement type',
      });
    }

    setLoading(true);
    try {
      const formData = new FormData();

      formData.append('title', inputs.title);
      formData.append('content', inputs.content);
      if (inputs.published_date) {
        formData.append('published_date', inputs.published_date);
      }
      if (thumbnail) {
        formData.append('thumbnail', thumbnail);
      }

      selectedTypes.forEach((t) => {
        formData.append('type', t);
      });

      selectedFiles.forEach((file) => {
        formData.append('attachments', file);
      });

      formData.append('deleteAttachments', JSON.stringify(deleteAttachments));

      const { data } = await axios.patch(
        `${url}/api/v1/admin/announcement/${announcement.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        setLoading(false);
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: `${data.message}`,
        });
        setLoading(false);
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err.response?.data?.message || 'Something went wrong'}`,
      });
      setLoading(false);
    }
  };

  const typeDisplay = selectedTypes.join(', ') || 'Announcement';

  return (
    <>
      <Helmet>
        <title>Atab - Update {typeDisplay}</title>
      </Helmet>

      <Box
        onSubmit={handleSubmit(onSubmit)}
        component='form'
        sx={{ p: 2, mt: 1 }}
      >
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} item>
            <TextField
              margin='normal'
              fullWidth
              required
              id='title'
              label='Enter a title'
              {...register('title')}
              type='text'
              autoFocus
              variant='outlined'
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              margin='normal'
              fullWidth
              id='published_date'
              label='Published Date'
              type='date'
              InputLabelProps={{ shrink: true }}
              {...register('published_date')}
              variant='outlined'
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              margin='normal'
              fullWidth
              id='file'
              label='Upload New Attachments (PDF or Image)'
              inputProps={{ multiple: true, accept: 'image/*,application/pdf' }}
              InputLabelProps={{ shrink: true }}
              autoComplete='file'
              type='file'
              onChange={handleFileChange}
            />
          </Grid>

          {/* Announcement Type Selection */}
          <Grid item xs={12} sm={6}>
            <FormControl component='fieldset' variant='standard'>
              <FormLabel component='legend' sx={{ fontWeight: 600, mb: 0.5 }}>
                Announcement Type(s)
              </FormLabel>
              <FormGroup row sx={{ gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTypes.includes('NOTICE')}
                      onChange={() => handleTypeToggle('NOTICE')}
                    />
                  }
                  label='NOTICE'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTypes.includes('NEWS')}
                      onChange={() => handleTypeToggle('NEWS')}
                    />
                  }
                  label='NEWS'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTypes.includes('MEMBER_NOTICE')}
                      onChange={() => handleTypeToggle('MEMBER_NOTICE')}
                    />
                  }
                  label='MEMBER NOTICE'
                />
              </FormGroup>
            </FormControl>
          </Grid>

          {selectedFiles?.length > 0 && (
            <Grid item xs={12}>
              <Box display='flex' flexWrap='wrap' gap={2} mt={2}>
                {selectedFiles?.map((file, index) =>
                  file.type.startsWith('image/') ? (
                    <img
                      key={index}
                      src={URL.createObjectURL(file)}
                      alt={`preview-${index}`}
                      width={50}
                      height={50}
                      style={{
                        borderRadius: 8,
                        objectFit: 'cover',
                        boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                      }}
                    />
                  ) : (
                    <Box
                      key={index}
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: '14px',
                      }}
                    >
                      📄 {file.name}
                    </Box>
                  )
                )}
              </Box>
            </Grid>
          )}

          {announcement?.attachments && announcement.attachments.length > 0 && (
            <Grid item xs={12}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
                Existing Attachments
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {announcement.attachments.map((attachment) => {
                  const isPdf = attachment.url.toLowerCase().endsWith('.pdf');
                  const fileName = attachment.url.split('/').pop() || '';
                  const isDeleted = deleteAttachments.includes(attachment.id);

                  return (
                    <Box
                      key={attachment.id}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        px: 1.2,
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: isDeleted ? '#fee2e2' : '#e2e8f0',
                        bgcolor: isDeleted ? '#fef2f2' : '#f8fafc',
                        opacity: isDeleted ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: isDeleted ? '#94a3b8' : '#475569',
                          textDecoration: isDeleted ? 'line-through' : 'none',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={fileName}
                      >
                        {isPdf ? '📄' : '🖼️'} {fileName}
                      </Typography>
                      {!isDeleted && (
                        <Box
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '11px !important',
                              fontWeight: '600 !important',
                              color: '#2563eb !important',
                              pr: '0px !important',
                            },
                          }}
                        >
                          <CommonPdfAndImageVeiw
                            file={attachment.url}
                            fileName='View'
                          />
                        </Box>
                      )}
                      {isDeleted ? (
                        <IconButton
                          size='small'
                          onClick={() => {
                            setDeleteAttachments((prev) =>
                              prev.filter((id) => id !== attachment.id)
                            );
                          }}
                          sx={{
                            p: 0.25,
                            color: '#10b981',
                            '&:hover': {
                              bgcolor: '#d1fae5',
                            },
                          }}
                        >
                          <span
                            style={{ fontSize: '14px', fontWeight: 'bold' }}
                          >
                            ⟲
                          </span>
                        </IconButton>
                      ) : (
                        <IconButton
                          size='small'
                          onClick={() => {
                            setDeleteAttachments((prev) => [
                              ...prev,
                              attachment.id,
                            ]);
                          }}
                          sx={{
                            p: 0.25,
                            color: '#ef4444',
                            '&:hover': {
                              bgcolor: '#fee2e2',
                            },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': { borderColor: 'primary.main' },
              }}
              onClick={() => document.getElementById('fileInput')?.click()}
            >
              {!thumbnail && !announcement?.thumbnail ? (
                <Typography color='textSecondary' align='center'>
                  Click to upload thumbnail
                </Typography>
              ) : (
                <img
                  src={
                    thumbnail
                      ? URL.createObjectURL(thumbnail)
                      : `${imgUrl}/${announcement.thumbnail}`
                  }
                  alt='thumbnail'
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}

              <input
                id='fileInput'
                type='file'
                accept='image/*'
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setThumbnail(e.target.files[0]);
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='Content'
              placeholder='Enter some details about this announcement here..'
              multiline
              {...register('content')}
              rows={5}
              maxRows={10}
              fullWidth
            />
          </Grid>
        </Grid>

        {loading ? (
          <Button
            disabled
            fullWidth
            variant='contained'
            disableElevation
            sx={{
              mt: 3,
              mb: 2,
              py: 1,
            }}
          >
            Loading...
          </Button>
        ) : (
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            sx={{
              mt: 3,
              mb: 2,
              py: 1,
            }}
          >
            Update {typeDisplay}
          </Button>
        )}
      </Box>
    </>
  );
};

export default UpdateAnnoucement;
